import { useCallback, useEffect, useRef, useState } from "react";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { FileUpload } from "primereact/fileupload";
import { Message } from "primereact/message";

import { useFlags } from "launchdarkly-react-client-sdk";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import {
  getTenantIdp,
  getIdpSettings,
  updateTenantSettings,
  updateTenantIdp,
} from "sj-ui-toolkit/src/api/admin/settings";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

import { EmailPasswordAuthCard } from "@/components/settings/EmailPasswordAuthCard";

interface IdpData {
  uuid: string;
  friendlyName: string;
  ssoUrl?: string;
  metadata?: string;
}

interface TenantIdpData {
  idp: IdpData;
  domains: string[];
}

const SKIP_SSO_CONFIRMATION = "skip_sso_confirmation";

function SsoConfirmationModal({
  isOpen,
  onCancel,
  onConfirm,
  title,
  description,
  confirmationText,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmationText: string;
}) {
  const [checked, setChecked] = useState(false);

  const handleConfirm = () => {
    if (checked) {
      localStorage.setItem(SKIP_SSO_CONFIRMATION, "true");
    }
    onConfirm();
  };

  return (
    <Dialog
      closable={false}
      visible={isOpen}
      onHide={onCancel}
      header={
        <div className="flex items-center gap-2">
          <i className="pi pi-exclamation-circle text-xl" />
          <span className="font-semibold">{title}</span>
        </div>
      }
      footer={
        <div className="flex gap-2 justify-end">
          <Button label="Cancel" onClick={onCancel} outlined />
          <Button label="Confirm" onClick={handleConfirm} severity="danger" />
        </div>
      }
      modal
    >
      <div className="flex flex-col gap-4">
        <p>{description}</p>
        <p>{confirmationText}</p>
        <div className="flex items-center gap-2">
          <Checkbox
            inputId="confirmation-switch"
            onChange={e => setChecked(e.checked ?? false)}
            checked={checked}
          />
          <label htmlFor="confirmation-switch">Don't show this message again</label>
        </div>
      </div>
    </Dialog>
  );
}

function UpdateSSOConfigModal({
  visible,
  onHide,
  idpData,
  onSuccess,
  showToast,
}: {
  visible: boolean;
  onHide: () => void;
  idpData: IdpData;
  onSuccess: (payload: { ssoUrl?: string; metadata?: string }) => void;
  showToast: (
    severity: "success" | "info" | "warn" | "error",
    summary: string,
    detail: string
  ) => void;
}) {
  const fileUploadRef = useRef<FileUpload>(null);
  const [ssoUrl, setSsoUrl] = useState("");
  const [metadataFile, setMetadataFile] = useState({ name: "", size: 0, content: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetState = () => {
    setSsoUrl("");
    setMetadataFile({ name: "", size: 0, content: "" });
    setError(null);
    setSaving(false);
    fileUploadRef.current?.clear();
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const handleFileSelect = (event: { files: File[] }) => {
    const file = event.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setMetadataFile({
          name: file.name,
          size: file.size,
          content: String(reader.result),
        });
        setSsoUrl("");
      };
      reader.readAsText(file);
    }
  };

  const handleClearFile = () => {
    setMetadataFile({ name: "", size: 0, content: "" });
    fileUploadRef.current?.clear();
  };

  const handleSsoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSsoUrl(e.target.value);
    if (e.target.value) {
      handleClearFile();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    if (!ssoUrl && !metadataFile.content) {
      setError("Please provide either a new SSO URL or upload a metadata file.");
      setSaving(false);
      return;
    }

    const idp = {
      uuid: idpData.uuid,
      friendlyName: idpData.friendlyName,
      ...(ssoUrl ? { ssoUrl } : {}),
      ...(metadataFile.content ? { metadata: metadataFile.content } : {}),
    };

    const tenantName = getCurrentSelectedTenant();
    if (!tenantName) {
      setError("Tenant name is not available");
      setSaving(false);
      return;
    }

    const { error: apiError } = await updateTenantIdp(tenantName, idp as any);

    if (apiError) {
      setError(apiError.message || "Failed to update SSO configuration");
      setSaving(false);
      return;
    }

    showToast("success", "Success", "SSO configuration updated successfully");
    onSuccess({
      ssoUrl: ssoUrl || undefined,
      metadata: metadataFile.content || undefined,
    });
    handleClose();
  };

  const hasSelection = !!ssoUrl || !!metadataFile.content;

  return (
    <Dialog
      header="Update SSO Configuration"
      visible={visible}
      onHide={handleClose}
      footer={
        <div className="flex gap-2">
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={handleClose}
            className="p-button-text"
          />
          {hasSelection && (
            <Button
              label="Clear"
              icon="pi pi-undo"
              onClick={resetState}
              className="p-button-text"
            />
          )}
          <Button
            label="Save"
            icon="pi pi-check"
            onClick={handleSave}
            loading={saving}
            disabled={!hasSelection}
          />
        </div>
      }
      modal
      style={{ width: "40rem", maxWidth: "95vw" }}
    >
      <div className="flex flex-col gap-4">
        {error && <Message severity="error" text={error} />}

        {!metadataFile.content && (
          <div>
            <label htmlFor="sso-url" className="block mb-1 font-medium">
              SSO URL
            </label>
            <InputText
              id="sso-url"
              value={ssoUrl}
              onChange={handleSsoUrlChange}
              className="w-full"
              placeholder="Enter new SSO URL"
              aria-required="true"
            />
          </div>
        )}

        {!ssoUrl && (
          <>
            {!metadataFile.content && (
              <div className="flex items-center gap-2">
                <hr className="flex-1" />
                <span className="body-sm text-muted">or</span>
                <hr className="flex-1" />
              </div>
            )}

            <div>
              <label className="block mb-1 font-medium">
                {metadataFile.content ? "Metadata File" : "Upload Metadata File"}
              </label>
              {metadataFile.name ? (
                <div className="flex items-center gap-2">
                  <span className="text-success">
                    {metadataFile.name.substring(0, 270)} (
                    {metadataFile.size ? `${(metadataFile.size / 1024).toFixed(2)} KB` : ""})
                  </span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-sm p-button-danger"
                    onClick={handleClearFile}
                    tooltip="Remove file"
                    aria-label="Remove file"
                  />
                </div>
              ) : (
                <FileUpload
                  ref={fileUploadRef}
                  name="metadataFile"
                  accept=".xml"
                  maxFileSize={1000000}
                  customUpload
                  onSelect={handleFileSelect}
                  auto
                  chooseLabel="Choose File"
                  emptyTemplate={
                    <p className="m-0 text-center text-secondary">
                      Drag and drop a metadata XML file here
                    </p>
                  }
                  pt={{
                    buttonbar: { className: "border-none bg-transparent pb-0" },
                    content: {
                      className: "border-1 border-dashed border-round surface-border p-4",
                    },
                  }}
                  headerTemplate={options => {
                    const { chooseButton } = options;
                    return (
                      <div className="flex items-center bg-transparent border-none p-0">
                        {chooseButton}
                      </div>
                    );
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const SSOConnection = () => {
  const { useNewCognitoAuthSetting } = useFlags();
  const pageTitle = useNewCognitoAuthSetting ? "Login Settings" : "SSO";

  useBreadcrumb({
    label: pageTitle,
    route: useNewCognitoAuthSetting
      ? AdminAppRoutes.routes.connections.routes.loginSettings.route
      : AdminAppRoutes.routes.connections.routes.sso.route,
    display: "page",
  });

  const toast = useRef<Toast>(null);

  const [toggleDisabled, setToggleDisabled] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [autoProvisionEnabled, setAutoProvisionEnabled] = useState(false);
  const [redirectActive, setRedirectActive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showUpdateConfig, setShowUpdateConfig] = useState(false);
  const [tenantIdpData, setTenantIdpData] = useState<TenantIdpData>({
    idp: { uuid: "", friendlyName: "" },
    domains: [],
  });
  const [loadingData, setLoadingData] = useState(true);
  const [ssoToggleNotice, setSsoToggleNotice] = useState("");

  const showLocalToast = useCallback(
    (
      severity: "success" | "info" | "warn" | "error",
      summary: string,
      detail: string,
      life = 3000
    ) => {
      toast.current?.show({ severity, summary, detail, life });
    },
    []
  );

  useEffect(() => {
    if (tenantIdpData.idp?.ssoUrl || tenantIdpData.idp?.metadata) {
      setIsConfigured(true);
    }
  }, [tenantIdpData]);

  useEffect(() => {
    if (!loadingData) {
      if (!ssoEnabled) {
        if (tenantIdpData?.domains?.length === 0) {
          setToggleDisabled(true);
          setSsoToggleNotice(
            "SSO is disabled for this tenant. Contact support@securityjourney.com to enable."
          );
        } else {
          setToggleDisabled(false);
          setSsoToggleNotice("Enable SSO to configure settings");
        }
      } else {
        if (!isConfigured) {
          setSsoToggleNotice("Please configure metadata to complete SSO setup");
        } else {
          setSsoToggleNotice("");
        }
      }
    }
  }, [loadingData, tenantIdpData, ssoEnabled, isConfigured]);

  useEffect(() => {
    const fetchTenantIdpData = async () => {
      setLoadingData(true);
      try {
        const tenantName = getCurrentSelectedTenant();
        if (tenantName) {
          const { data, error } = await getTenantIdp(tenantName);
          if (error) throw error;
          if (data) setTenantIdpData(data as TenantIdpData);
        } else {
          throw new Error("Tenant name is not available");
        }
      } catch (error) {
        console.error("Failed to load tenant IdP data", error);
        setToggleDisabled(true);
        setSsoToggleNotice(
          "Unable to load SSO settings. Contact support@securityjourney.com for assistance."
        );
      } finally {
        setLoadingData(false);
      }
    };

    fetchTenantIdpData();
  }, []);

  useEffect(() => {
    const fetchTenantIdpSettings = async () => {
      try {
        const tenantName = getCurrentSelectedTenant();
        if (tenantName && tenantIdpData.idp.uuid) {
          const { data: settings, error } = await getIdpSettings(
            tenantName,
            tenantIdpData.idp.uuid
          );
          if (error) throw error;
          setSsoEnabled(settings?.tenantIdpSettings?.enabled ?? false);
          setAutoProvisionEnabled(settings?.tenantIdpSettings?.autoProvision ?? false);
        }
      } catch (error) {
        console.error("Failed to load IdP settings", error);
        showLocalToast("error", "Error", "An unexpected error occurred", 5000);
      }
    };

    if (tenantIdpData.idp.uuid) {
      fetchTenantIdpSettings();
    }
  }, [tenantIdpData.idp.uuid, showLocalToast]);

  const updateSsoSetting = async (newValue: boolean) => {
    try {
      const tenantName = getCurrentSelectedTenant();
      if (tenantName && tenantIdpData.idp.uuid) {
        const tenantIdpSettings = {
          tenant: tenantName,
          idpUuid: tenantIdpData.idp.uuid,
          enabled: newValue,
          autoProvision: autoProvisionEnabled,
        };

        const { error } = await updateTenantSettings(tenantIdpSettings);

        if (error) {
          throw error;
        }

        showLocalToast("success", "Success", "SSO setting updated successfully");
      } else {
        throw new Error("Tenant name or IdP UUID is not available");
      }
    } catch (error: any) {
      console.error("Error updating SSO setting:", error);
      showLocalToast("error", "Error", error?.message || "Failed to update SSO setting", 5000);
      setSsoEnabled(!newValue);
    }
  };

  const handleSsoSwitch = () => {
    const dontShowConfirmation = localStorage.getItem(SKIP_SSO_CONFIRMATION);

    if (ssoEnabled) {
      if (dontShowConfirmation) {
        updateSsoSetting(false);
        setSsoEnabled(false);
      } else {
        setIsModalOpen(true);
      }
    } else {
      // If already configured, persist to backend immediately.
      // Otherwise, let the UI reflect enabled state so the user can configure;
      // handleConfigUpdateSuccess will call updateSsoSetting(true) once config is saved.
      if (isConfigured) {
        updateSsoSetting(true);
      }
      setSsoEnabled(true);
    }
  };

  const handleConfirmDisable = () => {
    updateSsoSetting(false);
    setSsoEnabled(false);
    setIsModalOpen(false);
  };

  const handleCancelDisable = () => {
    setIsModalOpen(false);
  };

  const handleAutoProvisionSwitch = async (newValue: boolean) => {
    setAutoProvisionEnabled(newValue);

    try {
      const tenantName = getCurrentSelectedTenant();
      if (tenantName && tenantIdpData.idp.uuid) {
        const tenantIdpSettings = {
          tenant: tenantName,
          idpUuid: tenantIdpData.idp.uuid,
          autoProvision: newValue,
          enabled: ssoEnabled,
        };

        const { error } = await updateTenantSettings(tenantIdpSettings);

        if (error) {
          throw error;
        }

        showLocalToast("success", "Success", "Auto Provision setting updated successfully");
      } else {
        throw new Error("Tenant name or IdP UUID is not available");
      }
    } catch (error: any) {
      console.error("Error updating auto provision setting:", error);
      showLocalToast(
        "error",
        "Error",
        error?.message || "Failed to update Auto Provision setting",
        5000
      );
      setAutoProvisionEnabled(!newValue);
    }
  };

  const handleConfigUpdateSuccess = ({
    ssoUrl,
    metadata,
  }: {
    ssoUrl?: string;
    metadata?: string;
  }) => {
    setTenantIdpData(prev => ({
      ...prev,
      idp: {
        ...prev.idp,
        ssoUrl: ssoUrl || undefined,
        metadata: metadata || undefined,
      },
    }));
    if (!isConfigured) {
      setIsConfigured(true);
      updateSsoSetting(true);
    }
  };

  return (
    <>
      <Toast ref={toast} />

      <div className="mb-4">
        <h1>{pageTitle}</h1>
      </div>

      {useNewCognitoAuthSetting && <EmailPasswordAuthCard />}

      <Card>
        {/* SSO */}

        <div className="flex justify-between items-center mb-2">
          <h2 className="font-semibold text-lg">SSO & SAML</h2>
          <InputSwitch
            checked={ssoEnabled}
            onChange={() => handleSsoSwitch()}
            disabled={toggleDisabled}
          />
        </div>

        {ssoToggleNotice && <p className="text-secondary mb-2">{ssoToggleNotice}</p>}
        <div className="flex flex-col gap-6">
          {ssoEnabled && (
            <div className="flex flex-col gap-3">
              <p className="body-sm">
                Security Journey integrates with any SAML 2.0 Identity Provider (IdP). View the{" "}
                <a
                  href="https://help.securityjourney.com/configuration-and-setup#sso"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  SSO help guides
                </a>{" "}
                for setup instructions and help with managing single sign-on.
              </p>

              {tenantIdpData.idp.ssoUrl && (
                <div>
                  <label className="block mb-1 font-medium">SSO URL</label>
                  <InputText value={tenantIdpData.idp.ssoUrl} disabled className="w-full" />
                </div>
              )}

              {tenantIdpData.idp.metadata && !tenantIdpData.idp.ssoUrl && (
                <div>
                  <label className="block mb-1 font-medium">Metadata</label>
                  <InputTextarea
                    value={tenantIdpData.idp.metadata}
                    readOnly
                    className="w-full"
                    rows={5}
                  />
                </div>
              )}

              <div>
                <Button
                  label="Update Config"
                  icon="pi pi-cog"
                  severity="secondary"
                  outlined
                  onClick={() => setShowUpdateConfig(true)}
                />
              </div>
            </div>
          )}

          {/* Auto Provision & Redirect */}
          {ssoEnabled && (
            <div className="flex flex-col gap-6">
              <h2 className="atlas-h4">Additional Settings</h2>

              <div className="flex flex-col gap-3">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <label className="font-medium">Auto Provision Users</label>
                    <p className="body-sm text-secondary">
                      Enable to automatically add anyone who successfully signs in via this IdP as a
                      licensed user in this tenant.
                    </p>
                  </div>
                  <InputSwitch
                    checked={autoProvisionEnabled}
                    onChange={e => handleAutoProvisionSwitch(e.value)}
                  />
                </div>

                <div className="flex justify-between items-start gap-3">
                  <div>
                    <label className="font-medium">Redirect from Sign-In Page Active</label>
                    <p className="body-sm text-secondary">
                      Anyone who enters an email address with the following domains will be
                      redirected to this IdP from the sign in page:
                    </p>
                    {!loadingData && tenantIdpData.domains.length > 0 && (
                      <ul className="list-disc list-inside body-sm mt-1">
                        {tenantIdpData.domains.map(domain => (
                          <li key={domain}>{domain}</li>
                        ))}
                      </ul>
                    )}
                    {!loadingData && tenantIdpData.domains.length === 0 && (
                      <p className="body-sm text-secondary mt-1">No domains configured</p>
                    )}
                    <p className="body-sm mt-2">
                      To make changes to this list or to opt-out, please contact{" "}
                      <a
                        href="mailto:support@securityjourney.com"
                        className="text-primary hover:underline"
                      >
                        support@securityjourney.com
                      </a>
                      .
                    </p>
                  </div>
                  <InputSwitch
                    checked={redirectActive}
                    onChange={e => setRedirectActive(e.value)}
                    disabled
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      <UpdateSSOConfigModal
        visible={showUpdateConfig}
        onHide={() => setShowUpdateConfig(false)}
        idpData={tenantIdpData.idp}
        onSuccess={handleConfigUpdateSuccess}
        showToast={showLocalToast}
      />

      <SsoConfirmationModal
        isOpen={isModalOpen}
        onCancel={handleCancelDisable}
        onConfirm={handleConfirmDisable}
        title="Disabling SSO"
        description="Disabling SSO will prevent users from signing in via this Identity Provider. Users will need to use their standard credentials to access the application."
        confirmationText="Would you like to continue?"
      />
    </>
  );
};

export default SSOConnection;
